import React, { useEffect, useState } from "react";
import { ExternalLink, Loader2, RefreshCw, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Row } from "../_ui";

const inputCls = "admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm";
const API = process.env.REACT_APP_BACKEND_URL;

function PageLinks({ site, pages }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="static-page-links">
      {pages.map((p) => (
        <a key={p.slug || "home"} data-testid={`static-page-link-${p.slug || "home"}`} href={`${API}${p.preview}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 border border-zinc-700 hover:border-white text-zinc-300 hover:text-white px-2 py-1 rounded-sm text-[11px] mono">
          {p.path} <ExternalLink size={10} />
        </a>
      ))}
    </div>
  );
}

function CopyControls({ site, status, reload, refresh }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  async function run(kind) {
    setBusy(kind); setMsg("");
    try {
      const { data } = await api.post(`/sites/${site.id}/static/${kind}`);
      setMsg(kind === "regenerate-copy" ? `✓ Reworded (cost $${(data.cost_usd || 0).toFixed(3)})` : "✓ Reset to default copy");
      await refresh(); await reload();
    } catch (e) { setMsg(`✕ ${e?.response?.data?.detail || "Failed"}`); }
    finally { setBusy(""); }
  }
  return (
    <div className="space-y-2">
      <div className="text-xs text-zinc-400" data-testid="static-copy-status">
        {status?.copy_generated_at
          ? <>AI reword applied <span className="mono text-zinc-300">{new Date(status.copy_generated_at).toLocaleString()}</span></>
          : status?.copy_error
            ? <span className="text-rose-300">Reword failed: {status.copy_error}</span>
            : "Default copy (AI reword pending or not run)"}
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="static-regenerate-copy-btn" disabled={!!busy} onClick={() => run("regenerate-copy")}
                className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40 px-3 py-1.5 rounded-sm text-xs font-semibold">
          {busy === "regenerate-copy" ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Regenerate copy (AI, ~$0.02)
        </button>
        <button data-testid="static-reset-copy-btn" disabled={!!busy || !status?.copy_customised} onClick={() => run("reset-copy")}
                className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 disabled:opacity-40 px-3 py-1.5 rounded-sm text-xs font-semibold">
          {busy === "reset-copy" ? <Loader2 className="animate-spin" size={12} /> : <RotateCcw size={12} />} Reset to default
        </button>
        {msg && <span data-testid="static-copy-msg" className="text-xs mono text-zinc-300">{msg}</span>}
      </div>
      <div className="text-[11px] text-zinc-500">Takes ~30 s. Legal pages stay verbatim; only marketing copy is reworded.</div>
    </div>
  );
}

/** Settings → static (lead-gen) site: form snippet, contact details, design, copy, pages. */
export default function StaticSitePanel({ site, form, setField, reload }) {
  const [opts, setOpts] = useState(null);
  const [status, setStatus] = useState(null);
  const refresh = async () => { try { setStatus((await api.get(`/sites/${site.id}/static`)).data); } catch { /* shown as unknown */ } };
  useEffect(() => {
    api.get("/sites/static/options").then((r) => setOpts(r.data)).catch(() => {});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);
  const len = (form.form_embed_html || "").length;
  return (
    <>
      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Lead form snippet (home page)</div>
      <Row label="JS form snippet">
        <div className="space-y-2">
          <textarea
            data-testid="static-form-embed"
            value={form.form_embed_html}
            onChange={(e) => setField("form_embed_html", e.target.value)}
            rows={8}
            spellCheck={false}
            placeholder={'<script src="https://forms.example.com/loader.js" data-key="…"></script>\n<div id="loan-form"></div>'}
            className={`${inputCls} text-xs mono leading-relaxed`}
            style={{ minHeight: 160, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          />
          <div className="flex items-center justify-between gap-3 text-[11px] mono">
            <span className="text-zinc-500">Rendered verbatim inside the home page&apos;s <span className="text-zinc-300">#offer</span> card (replaces the placeholder). Also settable on many sites at once via Sites → bulk “Form snippet”.</span>
            <span className={`shrink-0 ${len > 45000 ? "text-rose-400" : "text-zinc-500"}`}>{len.toLocaleString()} / 50,000</span>
          </div>
        </div>
      </Row>

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Contact details (legal pages)</div>
      <Row label="Support email">
        <input data-testid="static-support-email" value={form.support_email} onChange={(e) => setField("support_email", e.target.value)} placeholder={status?.support_email || `support@${site.domain || "your-domain.com"}`} className={`${inputCls} mono`} />
        <div className="text-[11px] text-zinc-500 mt-1">Blank = support@{site.domain || "{domain}"}.</div>
      </Row>
      <Row label="Business address">
        <input data-testid="static-business-address" value={form.business_address} onChange={(e) => setField("business_address", e.target.value)} placeholder="123 Main St, Suite 100, City, ST 00000" className={inputCls} />
        <div className="text-[11px] text-zinc-500 mt-1">Shown in the Privacy Policy, California Privacy Notice and Do-Not-Sell page. Blank = the postal-mail option is omitted.</div>
      </Row>

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Design (text + colors only, no images)</div>
      <Row label="Palette">
        <select data-testid="static-palette-select" value={form.static_palette} onChange={(e) => setField("static_palette", e.target.value)} className={inputCls}>
          {(opts?.palettes || []).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {opts && (
          <div className="flex gap-1.5 mt-2" data-testid="static-palette-swatches">
            {["primary", "accent", "hero", "bg"].map((k) => {
              const p = (opts.palettes || []).find((x) => x.key === form.static_palette);
              return <span key={k} title={k} className="w-6 h-6 rounded-sm border border-zinc-700" style={{ background: p?.[k] }} />;
            })}
          </div>
        )}
      </Row>
      <Row label="Font pair">
        <select data-testid="static-font-select" value={form.static_font} onChange={(e) => setField("static_font", e.target.value)} className={inputCls}>
          {(opts?.fonts || []).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </Row>

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Marketing copy (one-time AI reword)</div>
      <Row label="Copy"><CopyControls site={site} status={status} reload={reload} refresh={refresh} /></Row>

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Pages</div>
      <Row label="Preview">{status ? <PageLinks site={site} pages={status.pages} /> : <span className="text-xs text-zinc-500">…</span>}</Row>
    </>
  );
}
